/**
 * One factory for every outline's entity triple, replacing the three hand-written `OutlineEntityKinds`
 * the Service Plan, the Service Plan Template and the Visit each kept.
 *
 * All three now draw the SAME task entity — one `PlannedTask` record serves a live task, a template
 * task and a Visit exception — so the only things that still differ are which line entity the branch
 * rows are, which projection the tree is read from, which row-id prefixes key the two levels, and
 * whether the outline sits on a calendar. That is exactly this function's argument list.
 *
 * The task and outline entity names arrive as ARGUMENTS rather than being imported: the kit must never
 * reach into `workbuddy/entities/**`, and `ActivityEntityName.PlannedTask` is an entity-side constant
 * until the API phase puts it on the enum. Callers pass it; the kit stays boundary-clean.
 */
import { ActivityEntityName, Entity } from '@jms/model';
import { OutlineEntityKinds, OutlineRow } from '../outline.authoring.model';

/** What one outline is drawn against. Everything not named here is identical across all three. */
export interface OutlineKindsConfig {
  /** The branch entity whose rows own tasks — a Service Line, or its template twin. */
  line: ActivityEntityName;
  /** The one task entity every outline now draws. */
  task: ActivityEntityName;
  /** The legacy semantic task kind accepted as a subtask parent. */
  taskParent?: ActivityEntityName;
  /** The read projection the tree is queried from. */
  outline: ActivityEntityName;
  linePrefix: string;
  taskPrefix: string;
  /**
   * The field the projection names its owning branch reference under. The live and Visit projections
   * call it `serviceLine`, the template one `templateLine`; reading it by name here keeps that
   * difference in one place rather than normalising the tree on every load, which would break the row
   * identity AG Grid compares against.
   */
  lineField: 'serviceLine' | 'templateLine';
  /**
   * Whether this outline sits on a calendar, so its schedule chip can date the next visit. The live
   * plan does. A template does not — it defines a cadence with no start to count from. A Visit does
   * not either, for the opposite reason: a Visit IS the date, so there is no cadence to project
   * forward and a schedule chip would invite editing the plan's rhythm from one occurrence.
   */
  datesVisits?: boolean;
  /**
   * Whether rows on this outline carry a cadence at all. Defaults to true.
   *
   * The Visit's answer is no, and it is a different statement from `datesVisits`: a template has no
   * calendar to date the next visit against but genuinely carries a cadence, so it keeps its chip.
   * A Visit is ONE attendance — there is no rhythm to show at all.
   */
  hasSchedule?: boolean;
  /**
   * Extra params the child-create dialog needs beyond the owning line.
   *
   * Scope, not decoration. A Visit's create must carry `visitId`, because the same task editor saves
   * an exception for one attendance when it has one and edits the standing task when it does not — so
   * omitting it would silently write a child into the plan from a Visit's own `+`. The plan and
   * template outlines need nothing here: their line reference already IS the scope.
   */
  createParams?: Record<string, unknown>;
}

export function createOutlineKinds(config: OutlineKindsConfig): OutlineEntityKinds {

  const lineField = config.lineField;

  return {
    line: config.line,
    task: config.task,
    ...(config.taskParent ? { taskParent: config.taskParent } : {}),
    outline: config.outline,
    linePrefix: config.linePrefix,
    taskPrefix: config.taskPrefix,
    // The create dialog reads the owning line from a param named after the projection's own field, so
    // one value drives both the read accessor and the param key and the two cannot drift apart.
    lineParam: lineField,
    lineRef: (row: Partial<OutlineRow>) => (row as Record<string, Entity | undefined>)[lineField],
    ...(config.datesVisits === undefined ? {} : { datesVisits: config.datesVisits }),
    ...(config.hasSchedule === undefined ? {} : { hasSchedule: config.hasSchedule }),
    ...(config.createParams ? { createParams: config.createParams } : {}),
  };
}
